<template>
  <main ref="root" id="appRoot" class="fullscreen-wrapper" :class="{isLoading: appStore.isLoading}">
    <RouterView />
    <ModalsContainer />
    <TpeCollector />
    <div v-if="appStore.isLoading" class="app-loading">
      <IconSvg class="loader" :name="'loader'" />
    </div>
    <ModalConfirm :modal-id="appStore.pauseUserSessionModalId" title="Suspendre la session" @confirm="() => confirmPauseUserSession()">
        <div class="details">
            <p>L'activité en cours sera suspendue.</p>
            <p>Retrouvez votre session en vous connectant à nouveau</p>
        </div>
    </ModalConfirm>

    <ModalConfirm :modal-id="appStore.closeUserSessionModalId" title="Terminer la session" @confirm="() => confirmCloseUserSession()">
        <div class="details">
            <p>En terminant la session, vous clôturez également votre caisse.</p>

            <!-- Liste des sessions ouvertes (mode multi-sessions) -->
            <div v-if="openSessions.length > 1" class="sessions-list">
                <div class="sessions-list-header">
                    <strong>Sessions à clôturer ({{ selectedSessionIds.length }} / {{ openSessions.length }})</strong>
                    <button type="button" class="link-button" @click="toggleAllSessions">
                        {{ allSelected ? 'Tout désélectionner' : 'Tout sélectionner' }}
                    </button>
                </div>
                <div v-for="session in openSessions" :key="session._id" class="session-row">
                    <label>
                        <input
                            type="checkbox"
                            :value="session._id"
                            v-model="selectedSessionIds"
                        />
                        <span class="session-info">
                            <span class="session-event">{{ session.eventId?.name || 'Événement inconnu' }}</span>
                            <span v-if="session.organisationId?.name" class="session-org">
                                {{ session.organisationId.name }}
                            </span>
                            <span class="session-meta">
                                Session n°{{ session.sessionNumber }}
                                <span v-if="session.status === 'paused'" class="session-status">(suspendue)</span>
                            </span>
                        </span>
                    </label>
                </div>
            </div>

            <div class="cash-out-options">
                <div class="option">
                    <label>
                        <input type="checkbox" v-model="downloadCashOut" />
                        Télécharger la sortie de caisse
                    </label>
                </div>

                <div v-if="isThermalPrinterEnabled" class="option">
                    <label>
                        <input type="checkbox" v-model="printCashOut" />
                        Imprimer la sortie de caisse sur l'imprimante thermique
                    </label>
                </div>

                <div class="option">
                    <label>
                        <input type="checkbox" v-model="sendEmailCashOut" />
                        Envoyer par email
                    </label>
                    <input
                        v-if="sendEmailCashOut"
                        type="email"
                        v-model="emailCashOut"
                        placeholder="Email"
                        class="email-input"
                    />
                </div>

                <div class="option option-logout">
                    <label>
                        <input type="checkbox" v-model="logoutAfterClose" />
                        Se déconnecter après la clôture
                    </label>
                </div>
            </div>
        </div>
    </ModalConfirm>
  </main>
</template>

<script setup lang="ts">
  import { ModalsContainer, useVfm } from 'vue-final-modal'
import { useAppStore } from '@/stores/AppStore'
import { useUserStore } from '@/stores/UserStore'
import { usePrintStore, PrintModeEnum } from '@/stores/PrintStore'
import ModalConfirm from '@/components/molecules/modal/ModalConfirm.vue'
import IconSvg from '@/components/atoms/ASvg.vue'
import TpeCollector from '@/components/organisms/TpeCollector.vue'
import { useRouter } from 'vue-router'
import { onMounted, ref, computed, watch } from 'vue'

  const vfm = useVfm()
  const router = useRouter()

  const appStore = useAppStore()
  const userStore = useUserStore()
  const printStore = usePrintStore()

  // Cash-out options
  const downloadCashOut = ref(true)
  const printCashOut = ref(false)
  const sendEmailCashOut = ref(false)
  const emailCashOut = ref(userStore.me?.email || '')
  const logoutAfterClose = ref(true)

  // Sessions à clôturer (multi). Pré-sélectionnées par défaut dès que la
  // liste arrive (cf. watch ci-dessous). Le pré-chargement de la liste
  // est fait au clic sur "Terminer" dans Sidebar.vue / SessionLogout.vue.
  const selectedSessionIds = ref<string[]>([])
  const openSessions = computed(() => userStore.sellingSessions || [])
  const allSelected = computed(
    () =>
      openSessions.value.length > 0 &&
      selectedSessionIds.value.length === openSessions.value.length,
  )

  const toggleAllSessions = () => {
    if (allSelected.value) {
      selectedSessionIds.value = []
    } else {
      selectedSessionIds.value = openSessions.value.map((s) => s._id)
    }
  }

  // Pré-cocher toutes les sessions dès que la liste change (le clic sur
  // "Terminer" déclenche un getSellingSessions() qui met à jour le store).
  watch(
    openSessions,
    (sessions) => {
      selectedSessionIds.value = sessions.map((s) => s._id)
    },
    { immediate: true },
  )

  // Check if thermal printer is enabled
  const isThermalPrinterEnabled = computed(() => {
    return printStore.getPrinterMode === PrintModeEnum.OANDB_EPSON
  })

  onMounted(async () => {
    const isApiOk = await appStore.isApiOk()
    console.log('App mounted', isApiOk);
  })

  const confirmPauseUserSession = (async () => {
        userStore.logOut()
        vfm.close(appStore.pauseUserSessionModalId)
        router.push('login')
    })

    const confirmCloseUserSession = (async () => {
        // Si aucune session sélectionnée, on ne fait rien (UI devrait disable
        // le bouton mais filet de sécurité ici).
        if (selectedSessionIds.value.length === 0) {
            vfm.close(appStore.closeUserSessionModalId)
            return
        }

        // Mémoriser si la session courante (celle de l'event ouvert) fait
        // partie des sessions fermées : on doit alors rediriger l'opérateur
        // hors de la page de vente (qui n'a plus de sens sans session).
        const currentSessionId = userStore.sellingSession?._id
        const currentWasClosed =
            currentSessionId !== undefined &&
            selectedSessionIds.value.includes(currentSessionId)

        const result = await userStore.closeSessions(
            selectedSessionIds.value,
            {
                downloadPdf: downloadCashOut.value,
                emailTo: sendEmailCashOut.value ? emailCashOut.value : null,
                printThermal: printCashOut.value,
                logout: logoutAfterClose.value,
            },
        )

        vfm.close(appStore.closeUserSessionModalId)

        if (result.failed > 0) {
            // Erreurs partielles : ne pas rediriger automatiquement, laisser
            // l'opérateur voir l'état (toasts d'erreur déjà affichés).
            return
        }

        if (logoutAfterClose.value) {
            // Logout : retour à login (comportement standard).
            router.push('login')
        } else if (currentWasClosed) {
            // Pas de logout mais la session courante a été fermée :
            // rediriger vers la liste des événements de l'organisation
            // courante (l'opérateur peut en sélectionner un autre où il a
            // encore une session ouverte, ou en démarrer une nouvelle).
            const orgId = userStore.organisation?._id
            if (orgId) {
                router.push({ name: 'events', params: { organisationId: orgId } })
            } else {
                router.push({ name: 'organisations' })
            }
        }
        // Sinon (logout off + session courante intacte) : on reste sur la
        // page actuelle, l'opérateur continue son flow sur l'event courant.
    })
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variables';

.details {
  color: $white;
  font-size: 1.4rem;
}

.cash-out-options {
  margin-top: 20px;
  padding: 18px 20px;
  border-radius: 12px;
  border: 1px solid $grey-border;
  background: rgba($white, 0.03);
}

.cash-out-options .option {
  margin-bottom: 14px;
}

.cash-out-options .option:last-child {
  margin-bottom: 0;
}

.cash-out-options label {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 1.4rem;
  color: $white;
}

.cash-out-options input[type="checkbox"] {
  cursor: pointer;
  width: 18px;
  height: 18px;
  accent-color: $yellow-main;
  flex-shrink: 0;
}

.cash-out-options .email-input {
  margin-top: 10px;
  margin-left: 28px;
  padding: 10px 14px;
  border: 1px solid $grey-border;
  border-radius: 6px;
  font-size: 1.4rem;
  width: calc(100% - 28px);
  background: $bg-light;
  color: $white;

  &::placeholder {
    color: $grey-text;
  }

  &:focus {
    outline: none;
    border-color: $yellow-main;
  }
}

.sessions-list {
  margin-top: 20px;
  padding: 18px 20px;
  border-radius: 12px;
  border: 1px solid $grey-border;
  background: rgba($white, 0.03);
}

.sessions-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid $grey-border;
  font-size: 1.4rem;
  color: $white;

  strong {
    font-family: $font-title;
    text-transform: uppercase;
    font-size: 1.3rem;
    letter-spacing: 0.05em;
  }
}

.sessions-list-header .link-button {
  background: none;
  border: none;
  color: $yellow-main;
  cursor: pointer;
  font-size: 1.3rem;
  padding: 0;
  text-decoration: underline;
  font-family: $font-main;

  &:hover {
    color: lighten($yellow-main, 5%);
  }
}

.session-row {
  padding: 10px 0;
  border-bottom: 1px solid rgba($grey-border, 0.4);

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:first-child {
    padding-top: 0;
  }
}

.session-row label {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
}

.session-row input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
  flex-shrink: 0;
  accent-color: $yellow-main;
}

.session-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.session-event {
  font-size: 1.5rem;
  font-weight: 600;
  color: $white;
}

.session-org {
  font-size: 1.3rem;
  color: $white-light;
  font-style: italic;
}

.session-meta {
  font-size: 1.2rem;
  color: $white-light;
}

.session-status {
  color: $yellow-main;
  font-style: italic;
  margin-left: 6px;
}

.option-logout {
  margin-top: 4px;
  padding-top: 14px;
  border-top: 1px solid $grey-border;
}
</style>
